<template>
  <div :dir="dir" :class="dropdownClasses" class="dropdown v-select bootstrap-tagsinput">
    <div ref="toggle" :class="['dropdown-toggle', 'clearfix']" @click.prevent="toggleDropdown">
      <draggable
        :list="draggableValue"
        item-key="name"
        :class="inlineClasses"
        ghost-class="dragging"
        @change="draggableValue = $event"
      >
        <template #item="{ element }">
          <span class="tag label label-info">
            {{ getOptionLabel(element.name) }}
            <span
              v-if="multiple"
              class="remove-icon pointer"
              :disabled="disabled ? disabled : null"
              data-test="remove-tag-icon"
              data-role="remove"
              @click="deselect(element.name)"
            />
          </span>
        </template>
      </draggable>
      <div :class="inlineClasses" class="twitter-typeahead">
        <input
          ref="search"
          v-model="search"
          :data-test="`vselect-${name}-input`"
          :class="{ 'bgm-white': inline }"
          :disabled="disabled"
          :placeholder="placeholder || $t('Enter skills here')"
          :tabindex="tabindex"
          :readonly="!searchable"
          :maxlength="max"
          style="padding: 0 0 0 10px !important"
          type="search"
          class="form-control tt-input"
          autocomplete="off"
          @keydown.delete="maybeDeleteValue"
          @keyup.esc="onEscape"
          @keydown.up.prevent="typeAheadUp"
          @keydown.down.prevent="typeAheadDown"
          @keydown.enter.prevent="typeAheadSelect"
          @blur="onSearchBlur"
        />
        <input
          v-if="formId !== ''"
          v-model="mutableValue"
          :name="name"
          :form="formId"
          type="hidden"
        />
        <input v-else v-model="mutableValue" :name="name" type="hidden" />

        <transition :name="transition">
          <ul
            v-if="open && dropdownClasses.open"
            ref="dropdownMenu"
            :style="{ 'max-height': maxHeight }"
            class="dropdown-menu tt-dataset"
          >
            <template v-if="sortedOptions.length > 0">
              <li
                v-for="(option, index) in sortedOptions"
                :key="index"
                :class="{
                  active: isOptionSelected(option),
                  highlight: index === typeAheadPointer,
                }"
                class="tt-suggestion tt-selectable"
                @mouseover="typeAheadPointer = index"
              >
                <a @mousedown.prevent="select(option)">
                  <slot v-bind="{ option }" name="option">
                    <span class="capitalize">{{ getOptionLabel(option) }}</span>
                  </slot>
                </a>
              </li>
            </template>
            <li v-if="!sortedOptions.length" class="no-options">
              <slot name="no-options">{{ $t('No existing matches.') }}</slot>
            </li>
          </ul>
        </transition>
      </div>

      <button
        v-show="showClearButton && !showSearchButton"
        :disabled="disabled"
        type="button"
        class="clear"
        title="Clear selection"
        @click="clearSelection"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <button
        v-show="!showClearButton && showSearchButton"
        :disabled="disabled"
        type="button"
        class="clear"
        title="Clear selection"
        @click="onChange"
      >
        {{ $t('Search') }}
      </button>
      <slot name="spinner">
        <div v-show="mutableLoading" class="spinner">Loading...</div>
      </slot>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign,vue/no-boolean-default */

// Vue components
import draggable from 'vuedraggable'

const typeAheadPointer = {
  data() {
    return {
      typeAheadPointer: -1,
    }
  },

  watch: {
    filteredOptions: {
      deep: true,
      handler() {
        this.typeAheadPointer = -1
      },
    },
    search(val) {
      this.$emit('error', '')
    },
  },

  methods: {
    /**
     * Move the typeAheadPointer visually up the list by
     * subtracting the current index by one.
     * @return {void}
     */
    typeAheadUp() {
      if (this.typeAheadPointer >= 0) {
        this.typeAheadPointer -= 1
        this.adjustScroll()
      }
    },

    /**
     * Move the typeAheadPointer visually down the list by
     * adding the current index by one.
     * @return {void}
     */
    typeAheadDown() {
      if (this.typeAheadPointer < this.filteredOptions.length - 1) {
        this.typeAheadPointer += 1
        this.adjustScroll()
      }
    },

    adjustScroll() {
      const dropdown = this.$el.querySelector('.dropdown-menu')
      const highlight = this.$el.querySelector('.highlight')
      if (highlight) {
        const scroll = (this.typeAheadPointer + 2) * highlight.clientHeight - dropdown.clientHeight
        dropdown.scrollTop = scroll < 0 ? 0 : scroll
      }
    },

    /**
     * Select the option at the current typeAheadPointer position.
     * Optionally clear the search input on selection.
     * @return {void}
     */
    typeAheadSelect() {
      if (!this.open) {
        // If we are not open
        return // don't do anything
      }
      if (this.filteredOptions[this.typeAheadPointer]) {
        this.select(this.filteredOptions[this.typeAheadPointer])
        return
      }
      if (this.taggable && this.search.length) {
        this.select(this.search)
        return
      }
      if (
        this.canAddUnsupportedOptions &&
        !this.valueAsArrayLowerCase.includes(this.search.toLowerCase()) &&
        this.search.replace(/ /g, '').length > 0
      ) {
        this.mutableValue.push(this.search)
        this.search = ''
      }
      if (this.canAddUnsupportedOptions && this.clearSearchOnSelect) {
        this.search = ''
      }
    },
  },
}
export default {
  name: 'VSelect',
  components: {
    draggable,
  },
  mixins: [typeAheadPointer],
  props: {
    /**
     * Contains the currently selected value. Very similar to a
     * `value` attribute on an <input>. You can listen for changes
     * using 'change' event using v-on
     * @type {Object||String||null}
     */
    modelValue: {
      default: null,
    },

    /**
     * An array of strings or objects to be used as dropdown choices.
     * If you are using an array of objects, vue-select will look for
     * a `label` key (ex. [{label: 'This is Foo', value: 'foo'}]). A
     * custom label key can be set with the `label` prop.
     * @type {Array}
     */
    options: {
      type: Array,
      default() {
        return ['sas', 'scss']
      },
    },

    /**
     * Disable the dropdown entirely.
     * @type {Boolean}
     */
     noDrop: {
      type: Boolean,
      default: false,
    },

    /**
     * When true, the component will automatically select the value when the input loses focus
     * if there's only one value and no pre-selected options.
     * @type {Boolean}
     */
    autoSelectOnBlur: {
      type: Boolean,
      default: true,
    },

    name: {
      type: String,
      default() {
        return 'skills'
      },
    },

    formId: {
      type: String,
      default() {
        return ''
      },
    },

    /**
     * Disable the entire component.
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Can the user clear the selected property?
     * @type {Boolean}
     */
    clearable: {
      type: Boolean,
      default: true,
    },

    /**
     * Sets the max-height property on the dropdown list.
     * @deprecated
     * @type {String}
     */
    maxHeight: {
      type: String,
      default: '400px',
    },

    /**
     * Enable/disable filtering the options.
     * @type {Boolean}
     */
    searchable: {
      type: Boolean,
      default: true,
    },

    /**
     * Set the maximum number of chars you can write into the input field
     * @type {Number}
     */
    max: {
      type: Number,
      default: -1, // -1 lets you write unlimited strings
    },

    /**
     * Equivalent to the `multiple` attribute on a `<select>` input.
     * @type {Boolean}
     */
    multiple: {
      type: Boolean,
      default: false,
    },

    /**
     * Equivalent to the `placeholder` attribute on an `<input>`.
     * @type {String}
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * Sets the tags to be inline with the search bar
     * @type {Boolean}
     */
    inline: {
      type: Boolean,
      default: false,
    },

    /**
     * Sets a Vue transition property on the `.dropdown-menu`. vue-select
     * does not include CSS for transitions, you'll need to add them yourself.
     * @type {String}
     */
    transition: {
      type: String,
      default: 'fade',
    },

    /**
     * Enables/disables clearing the search text when an option is selected.
     * @type {Boolean}
     */
    clearSearchOnSelect: {
      type: Boolean,
      default: true,
    },

    /**
     * Enables/disables ability to enter items not alreayd in the options
     * @type {Boolean}
     */
    canAddUnsupportedOptions: {
      type: Boolean,
      default: true,
    },

    /**
     * Close a dropdown when an option is chosen. Set to false to keep the dropdown
     * open (useful when combined with multi-select, for example)
     * @type {Boolean}
     */
    closeOnSelect: {
      type: Boolean,
      default: true,
    },

    /**
     * Tells vue-select what key to use when generating option
     * labels when each `option` is an object.
     * @type {String}
     */
    label: {
      type: String,
      default: 'label',
    },

    /**
     * Callback to generate the label text. If {option}
     * is an object, returns option[this.label] by default.
     * @type {Function}
     * @param  {Object || String} option
     * @return {String}
     */
    getOptionLabel: {
      type: Function,
      default(option) {
        if (typeof option === 'object') {
          if (!option.hasOwnProperty(this.label)) {
            /* eslint-disable-next-line no-console */
            return console.warn(
              `[vue-select warn]: Label key "option.${this.label}" does not
                            exist in options object ${JSON.stringify(option)}.\n
                            http://sagalbot.github.io/vue-select/#ex-labels`,
            )
          }
          if (this.label && option[this.label]) {
            return option[this.label]
          }
        }
        return option
      },
    },

    /**
     * An optional callback function that is called each time the selected
     * value(s) change. When integrating with Vuex, use this callback to trigger
     * an action, rather than using :value.sync to retreive the selected value.
     * @type {Function}
     * @param {Object || String} val
     */
    onChange: {
      type: Function,
      default(val) {
        this.$emit('update:modelValue', val)
      },
    },

    /**
     * Enable/disable creating options from searchInput.
     * @type {Boolean}
     */
    taggable: {
      type: Boolean,
      default: false,
    },

    /**
     * Set the tabindex for the input field.
     * @type {Number}
     */
    tabindex: {
      type: Number,
      default: null,
    },

    /**
     * When true, newly created tags will be added to
     * the options list.
     * @type {Boolean}
     */
    pushTags: {
      type: Boolean,
      default: false,
    },

    /**
     * When true, existing options will be filtered
     * by the search text. Should not be used in conjunction
     * with taggable.
     * @type {Boolean}
     */
    filterable: {
      type: Boolean,
      default: true,
    },

    /**
     * Callback to determine if the provided option should
     * match the current search text. Used to determine
     * if the option should be displayed.
     * @type   {Function}
     * @param  {Object || String} option
     * @param  {String} label
     * @param  {String} search
     * @return {Boolean}
     */
    filterBy: {
      type: Function,
      default(option, label, search) {
        return search.length === 1
          ? (label || '').toLowerCase() === search.toLowerCase()
          : (label || '').toLowerCase().indexOf(search.toLowerCase()) > -1
      },
    },

    /**
     * Callback to filter results when search text
     * is provided. Default implementation loops
     * each option, and returns the result of
     * this.filterBy.
     * @type   {Function}
     * @param  {Array} list of options
     * @param  {String} search text
     * @param  {Object} vSelect instance
     * @return {Boolean}
     */
    filter: {
      type: Function,
      default(options, search) {
        return options.filter((option) => {
          let label = this.getOptionLabel(option)
          if (typeof label === 'number') {
            label = label.toString()
          }
          return this.filterBy(option, label, search)
        })
      },
    },

    /**
     * Function that determines how to sort the
     * filtered options such that the most relevant
     * option is located at the top.
     * @type  {Function}
     * @param {Array} list of filtered options
     * @param {String} search text
     * @return {Array} resorted array
     */
    sortFiltered: {
      type: Function,
      default(filtered) {
        return filtered
      },
    },

    /**
     * User defined function for adding Options
     * @type {Function}
     */
    createOption: {
      type: Function,
      default(newOption) {
        if (typeof this.mutableOptions[0] === 'object') {
          newOption = {
            [this.label]: newOption,
          }
        }
        this.$emit('option:created', newOption)
        this.maybePushTag(newOption)
        return newOption
      },
    },

    showSearchButton: {
      type: Boolean,
      default: false,
    },

    /**
     * When false, updating the options will not reset the select value
     * @type {Boolean}
     */
    resetOnOptionsChange: {
      type: Boolean,
      default: false,
    },

    /**
     * When false you can't delete tags with backspace, and have to click on the X
     * @type {Boolean}
     */
    deleteTags: {
      type: Boolean,
      default: true,
    },

    /**
     * Sets the id of the input element.
     * @type {String}
     * @default {null}
     */
    inputId: {
      type: String,
    },

    /**
     * Sets RTL support. Accepts 'ltr', 'rtl', 'auto'.
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/dir
     * @type {String}
     * @default 'auto'
     */
    dir: {
      type: String,
      default: 'auto',
    },

    classes: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  emits: [
    'update:modelValue',
    'option:created',
    'update:selected',
    'optionDeselected',
    'search:blur',
    'search:focus',
    'error',
  ],
  data() {
    return {
      search: '',
      open: false,
      mutableValue: null,
      mutableOptions: [],
    }
  },

  computed: {
    /**
     * Classes to be output on .dropdown
     * @return {Object}
     */
    dropdownClasses() {
      return {
        open: this.dropdownOpen,
        single: !this.multiple,
        searching: this.searching,
        searchable: this.searchable,
        unsearchable: !this.searchable,
        loading: this.mutableLoading,
        rtl: this.dir === 'rtl',
        disabled: this.disabled,
        ...this.classes,
      }
    },

    inlineClasses() {
      return {
        inlinetags: this.inline,
      }
    },

    dragOptions() {
      return {
        group: 'tags',
        ghostClass: 'dragging',
      }
    },

    /**
     * If search text should clear on blur
     * @return {Boolean} True when single and clearSearchOnSelect
     */
    clearSearchOnBlur() {
      return this.clearSearchOnSelect && !this.multiple
    },

    /**
     * Return the current state of the
     * search input
     * @return {Boolean} True if non empty value
     */
    searching() {
      return this.search
    },

    /**
     * Return the current state of the
     * dropdown menu.
     * @return {Boolean} True if open
     */
    dropdownOpen() {
      return this.noDrop ? false : this.open && !this.mutableLoading
    },

    /**
     * The currently displayed options, filtered
     * by the search elements value. If tagging
     * true, the search text will be prepended
     * if it doesn't already exist.
     *
     * @return {array}
     */
    filteredOptions() {
      if (!this.filterable && !this.taggable) {
        return this.mutableOptions.slice()
      }
      let options = this.search.length
        ? this.filter(this.mutableOptions, this.search, this)
        : this.mutableOptions
      options = options.filter((option) => this.valueAsArray.indexOf(option) === -1)

      if (this.taggable && this.search.length && !this.optionExists(this.search)) {
        options.unshift(this.search)
      }
      return options.slice(0, 25) // Max return options in dropdown is 25
    },

    /**
     * The current displayed options, filtered and sorted
     * by the search elements value.
     */
    sortedOptions() {
      const options = this.filteredOptions
      if (options.length === 0) return []
      return this.sortFiltered(options, this.search)
    },

    /**
     * Check if there aren't any options selected.
     * @return {Boolean}
     */
    isValueEmpty() {
      if (this.mutableValue) {
        if (typeof this.mutableValue === 'object') {
          return !Object.keys(this.mutableValue).length
        }
        return !this.mutableValue.length
      }

      return true
    },

    /**
     * Return the current value in array format.
     * @return {Array}
     */
    valueAsArray: {
      get() {
        if (this.mutableValue) {
          return Object.values(this.mutableValue)
        }

        return []
      },
      set(newValue) {
        this.mutableValue = newValue
      },
    },

    /**
     * Return the current value in array format in lower case
     * @return {Array}
     */
    valueAsArrayLowerCase() {
      return this.valueAsArray.map((val) => val.toLowerCase())
    },

    draggableValue: {
      get() {
        return this.valueAsArray.map((el) => ({ name: el }))
      },
      set({ moved: { newIndex, oldIndex } }) {
        const arr = [...this.valueAsArray]
        // This line just moves the element around
        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0])
        this.valueAsArray = arr ?? this.valueAsArray // If it breaks when moving we just leave it as is
      },
    },
    /**
     * Determines if the clear button should be displayed.
     * @return {Boolean}
     */
    showClearButton() {
      return !this.multiple && this.clearable && !this.open && this.mutableValue != null
    },
  },

  watch: {
    /**
     * When the value prop changes, update
     * the internal mutableValue.
     * @param  {mixed} val
     * @return {void}
     */
    modelValue(val) {
      this.mutableValue = val
    },

    /**
     * Maybe run the onChange callback.
     * @param  {string|object} val
     * @param  {string|object} old
     * @return {void}
     */
    mutableValue: {
      handler(val, old) {
        if (typeof window.vueValueChange === 'function') {
          window.vueValueChange(val)
        }
        if (this.onChange && (this.multiple || val !== old)) {
          this.onChange(val)
        }
        this.$emit('update:selected', this.mutableValue)
      },
      deep: true,
    },

    /**
     * When options change, update
     * the internal mutableOptions.
     * @param  {array} val
     * @return {void}
     */
    options: {
      handler(val) {
        this.mutableOptions = val
      },
      deep: true,
    },

    /**
     * Maybe reset the mutableValue
     * when mutableOptions change.
     * @return {[type]} [description]
     */
    mutableOptions: {
      handler() {
        if (!this.taggable && this.resetOnOptionsChange) {
          this.mutableValue = this.multiple ? [] : null
        }
      },
      deep: true,
    },

    /**
     * Always reset the mutableValue when
     * the multiple prop changes.
     * @param  {Boolean} val
     * @return {void}
     */
    multiple(val) {
      this.mutableValue = val ? [] : null
    },

    search(val) {
      this.open = val.length >= 1
      if (val[val.length - 1] === ',') {
        if (
          this.mutableValue.indexOf(val.slice(0, -1)) === -1 &&
          val.slice(0, -1).replace(/ /g, '').length > 0
        ) {
          this.mutableValue.push(val.slice(0, -1))
          this.search = ''
        } else {
          this.search = val.slice(0, -1)
        }
      }
    },
  },

  mounted() {
    this.$refs.search.addEventListener('paste', this.handlePaste)
  },

  beforeUnmount() {
    this.$refs.search.removeEventListener('paste', this.handlePaste)
  },

  /**
   * Clone props into mutable values
   */
  created() {
    this.mutableValue = this.modelValue
    this.mutableOptions = this.options.slice(0)
    this.mutableLoading = this.loading
  },

  methods: {
    /**
     * Select a given option.
     * @param  {Object|String} option
     * @return {void}
     */
    select(option) {
      if (!this.isOptionSelected(option)) {
        if (this.getOptionLabel(option).replace(/ /g, '').length < 1) {
          return
        }

        if (this.taggable && !this.optionExists(option)) {
          option = this.createOption(option)
        }

        if (this.multiple && !this.mutableValue) {
          this.mutableValue = [option]
        } else if (this.multiple) {
          this.mutableValue.push(option)
        } else {
          this.mutableValue = option
        }
      }
      this.onAfterSelect(option)
    },

    /**
     * De-select a given option.
     * @param  {Object|String} option
     * @return {void}
     */
    deselect(option) {
      if (this.multiple) {
        let ref = -1
        this.mutableValue.forEach((val) => {
          if (
            val === option ||
            (typeof val === 'object' && val[this.label] === option[this.label])
          ) {
            ref = val
          }
        })
        const index = this.mutableValue.indexOf(ref)
        this.mutableValue.splice(index, 1)
        this.$emit('optionDeselected', ref)
      } else {
        this.mutableValue = null
      }
    },

    /**
     * Clears the currently selected value(s)
     * @return {void}
     */
    clearSelection() {
      this.mutableValue = this.multiple ? [] : null
    },

    /**
     * Called from this.select after each selection.
     * @return {void}
     */
    onAfterSelect() {
      if (this.closeOnSelect) {
        this.open = !this.open
      }

      if (this.clearSearchOnSelect) {
        this.search = ''
      }
    },

    /**
     * Toggle the visibility of the dropdown menu.
     * @param  {Event} e
     * @return {void}
     */
    toggleDropdown(e) {
      if (
        e.target === this.$refs.openIndicator ||
        e.target === this.$refs.search ||
        e.target === this.$refs.toggle ||
        e.target === this.$el
      ) {
        if (this.open) {
          this.$refs.search.blur() // dropdown will close on blur
        } else if (!this.disabled) {
          this.$refs.search.focus()
          if (this.search.length >= 2) {
            this.open = true
          }
        }
      }
    },

    /**
     * Check if the given option is currently selected.
     * @param  {Object|String}  option
     * @return {Boolean}        True when selected | False otherwise
     */
    isOptionSelected(option) {
      if (this.multiple && this.mutableValue) {
        let selected = false
        this.mutableValue.forEach((opt) => {
          if (typeof opt === 'object' && opt[this.label] === option[this.label]) {
            selected = true
          } else if (typeof opt === 'object' && opt[this.label] === option) {
            selected = true
          } else if (opt === option) {
            selected = true
          }
        })
        return selected
      }

      return this.mutableValue === option
    },

    /**
     * If there is any text in the search input, remove it.
     * Otherwise, blur the search input to close the dropdown.
     * @return {void}
     */
    onEscape() {
      if (!this.search.length) {
        this.$refs.search.blur()
      } else {
        this.search = ''
      }
    },

    /**
     * Close the dropdown on blur.
     * @emits  {search:blur}
     * @return {void}
     */
    onSearchBlur() {
      // If autoSelectOnBlur is enabled and there's text in the search field and either no value or empty array (for multiple)
      if (
        this.autoSelectOnBlur &&
        this.search.trim() &&
        (this.mutableValue === null || (this.multiple && !this.mutableValue.length))
      ) {
        const searchValue = this.search.trim()
        if (searchValue.replace(/ /g, '').length > 0) {
          if (this.taggable && !this.optionExists(searchValue)) {
            this.select(this.createOption(searchValue))
          } else {
            this.select(searchValue)
          }
        }
      } else if (this.clearSearchOnBlur) {
        this.search = ''
      }

      this.open = false
      this.$emit('search:blur')
    },

    /**
     * Open the dropdown on focus.
     * @emits  {search:focus}
     * @return {void}
     */
    onSearchFocus() {
      this.open = true
      this.$emit('search:focus')
    },

    /**
     * Delete the value on Delete keypress when there is no
     * text in the search input, & there's tags to delete
     * @return {this.value}
     */
    maybeDeleteValue() {
      if (!this.$refs.search.value.length && this.mutableValue && this.deleteTags) {
        if (this.multiple) {
          return this.mutableValue.pop()
        }
        this.mutableValue = null
      }
      return null
    },

    /**
     * Determine if an option exists
     * within this.mutableOptions array.
     *
     * @param  {Object || String} option
     * @return {boolean}
     */
    optionExists(option) {
      let exists = false

      this.mutableOptions.forEach((opt) => {
        if (typeof opt === 'object' && opt[this.label] === option) {
          exists = true
        } else if (opt === option) {
          exists = true
        }
      })

      return exists
    },

    /**
     * If push-tags is true, push the
     * given option to mutableOptions.
     *
     * @param  {Object || String} option
     * @return {void}
     */
    maybePushTag(option) {
      if (this.pushTags) {
        this.mutableOptions.push(option)
      }
    },

    /**
     * Process data directly pasted
     * to input to parse each item
     * individually
     *
     * @emits  {error}
     * @return {void}
     * @param {String} val
     */
    processPastedData(val) {
      if (!val.includes(',')) {
        this.search = val
        return
      }

      this.$emit('error', '')

      const values = val
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)

      const invalidItems = []

      values.forEach((item) => {
        if (item.length <= this.max) {
          if (this.mutableValue.indexOf(item) === -1) {
            this.mutableValue.push(item)
          }
        } else {
          invalidItems.push(item)
        }
      })

      if (invalidItems.length > 0) {
        this.$emit(
          'error',
          `${$t('The following items exceed the maximum length of :max characters:', {
            max: this.max,
          })} ${invalidItems.join(', ')}`,
        )
      }

      this.search = ''
    },

    /**
     * Handle pasted input event
     *
     * @return {void}
     * @param {Object} event
     */
    handlePaste(event) {
      event.preventDefault()
      if (event.clipboardData) {
        this.processPastedData(event.clipboardData.getData('text'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.inlinetags {
  float: left;
  width: auto;
  .tag {
    margin-left: 3px !important;
  }
  .tag:first-child {
    margin-left: 0px !important;
  }
}

.tag .remove-icon:after {
  content: 'x';
  padding: 0px 2px;
}
</style>
